package com.macrofocus.eflux.ui

import com.macrofocus.common.json.JsonArray
import com.macrofocus.common.json.JsonObject
import com.macrofocus.eflux.EFluxConfiguration
import com.macrofocus.eflux.ViewLoader
import com.macrofocus.eflux.network.EFluxNode
import com.macrofocus.eflux.network.NodeField
import com.macrofocus.eflux.network.logical.logicalBusIdOf
import com.macrofocus.eflux.network.physical.busIdOf
import com.macrofocus.eflux.toRawString
import com.macrofocus.eflux.ui.heatmap.createGeneratorHeatmapData
import com.macrofocus.eflux.ui.heatmap.createNodeHeatmapData
import deckgl.DeckGL
import deckgl.HeatmapLayer
import deckgl.HeatmapLayerProps
import deckgl.OrthographicView
import emotion.react.css
import js.coroutines.awaitCancellation
import js.objects.unsafeJso
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.async
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.mkui.react.useProperty
import org.molap.dataframe.DataFrame
import org.molap.questdb.readQuestDBJson
import react.*
import react.dom.html.ReactHTML
import web.cssom.Position
import web.cssom.pct
import web.html.HTMLDivElement
import web.resize.ResizeObserver
import kotlin.math.log2
import kotlin.math.min

external interface NodeHeatmapProps : Props {
    var configuration: EFluxConfiguration
    var viewLoader: ViewLoader
    var show: Boolean
    var worldX : Double
    var worldY : Double
    var worldWidth : Double
    var worldHeight : Double
}

public val NodeHeatmap = FC<NodeHeatmapProps> { props ->
    if(!props.show) {
        return@FC
    }

    val (onPanZoom, _) = useProperty(props.viewLoader.svgMatrix)

    val (backgroundNodes, _) = useProperty(props.configuration.heatmapNodes)
    val (getNetwork, _) = useProperty(props.viewLoader.efluxNetwork)
    val (generators, _) = useProperty(props.configuration.generators)

    val screenScaleX : Double
    val screenTransX : Double
    val screenTransY : Double
    if(onPanZoom != null) {
        screenScaleX = onPanZoom.a
        screenTransX = onPanZoom.e
        screenTransY = onPanZoom.f
    } else {
        screenScaleX = 1.0
        screenTransX = 0.0
        screenTransY = 0.0
    }

    val views = OrthographicView()
    val (screenWidth, setScreenWidth) = useState { 1600 }
    val (screenHeight, setScreenHeight) = useState { 1200 }

    val worldX = props.worldX
    val worldY = props.worldY
    val worldWidth = props.worldWidth - worldX
    val worldHeight = props.worldHeight - worldY

    val world2pixelRatio = min(screenWidth / worldWidth, screenHeight / worldHeight)

    val actualWorldWidth = screenWidth / world2pixelRatio
    val actualWorldHeight = screenHeight / world2pixelRatio

    val aspectScale = world2pixelRatio * screenScaleX
    val zoom = log2(aspectScale)

    val worldShiftX = (actualWorldWidth - worldWidth) / 2
    val worldShiftY = (actualWorldHeight - worldHeight) / 2

    val screenToWorldX = { it : Double ->
        ((it / screenScaleX) * actualWorldWidth / screenWidth) - worldShiftX
    }

    val screenToWorldY = { it : Double ->
        ((it / screenScaleX) * actualWorldHeight / screenHeight) - worldShiftY
    }

    val worldX1 = screenToWorldX(-screenTransX)
    val worldY1 = screenToWorldY(-screenTransY)

    val worldX2 = screenToWorldX(-screenTransX + screenWidth.toDouble())
    val worldY2 = screenToWorldY(-screenTransY + screenHeight.toDouble())

    val worldMidX = (worldX1 + worldX2) / 2
    val worldMidY = (worldY1 + worldY2) / 2

    val INITIAL_VIEW_STATE : dynamic = unsafeJso {
        target = arrayOf(worldMidX, worldMidY, 0)
        this.zoom = zoom
    }

    val normalized = backgroundNodes == NodeField.va || backgroundNodes == NodeField.vm
    val negative = backgroundNodes == NodeField.va || backgroundNodes == NodeField.vm
    val clearSplitAtZero = false // backgroundNodes == NodeField.vm
    val intensity = when(backgroundNodes) {
        NodeField.va -> 10
        NodeField.vm -> 10
        else -> 1
    }

    val (data, setData) = useState(null as Array<dynamic>?)
    useEffect(backgroundNodes, getNetwork) {
        val array = createNodeHeatmapData(getNetwork?.nodes, backgroundNodes, { node ->
            val svgElement = node.unsafeCast<EFluxNode>().elements[0]
            val rect = svgElement.getBBox()
            arrayOf(rect.x + (rect.width / 2) - worldX, rect.y + (rect.height / 2) - worldY).unsafeCast<JsonArray>()
          }, normalized)
        setData(array)
    }

    val (getShiftedDateTime, _) = useProperty(props.configuration.maxDateTime)
    val (generatorsData, setGeneratorsData) = useState(null as Array<dynamic>?)
    useEffect(getShiftedDateTime, getNetwork, generators) {
        if(generators) {
            GlobalScope.launch {
                val dataFrame = window.fetch("/db/generators/${getShiftedDateTime.toRawString()}").then {
                    async { DataFrame.readQuestDBJson(it.json().await().unsafeCast<JsonObject>()) }
                }.await().await()

                val getCoordinates: (Int, DataFrame<Int, String, Any?>) -> JsonArray? = { row, dataFrame ->
                    val logicalBusId = logicalBusIdOf(busIdOf(dataFrame.getValueAt(row, "busId") as String))
                    val node = getNetwork?.graphicalNodes?.get(logicalBusId)
                    if (node != null) {
                        val svgElement = node.unsafeCast<EFluxNode>().elements[0]
                        val rect = svgElement.getBBox()
                        arrayOf(
                            rect.x + (rect.width / 2) - worldX,
                            rect.y + (rect.height / 2) - worldY
                        ).unsafeCast<JsonArray>()
                    } else {
                        null
                    }
                }
                val generatorHeatmapData = createGeneratorHeatmapData(dataFrame, getCoordinates)
                setGeneratorsData(generatorHeatmapData)
            }
        }
    }

    val RdYlBu = arrayOf(
        arrayOf(165,0,38),
        arrayOf(215,48,39),
        arrayOf(244,109,67),
        arrayOf(253,174,97),
        arrayOf(254,224,144),
        arrayOf(255,255,191, 0),
        arrayOf(224,243,248),
        arrayOf(171,217,233),
        arrayOf(116,173,209),
        arrayOf(69,117,180),
        arrayOf(49,54,149)
    ).reversedArray()

    val RdGy = arrayOf(
        arrayOf(103,0,31),
        arrayOf(178,24,43),
        arrayOf(214,96,77),
        arrayOf(244,165,130),
        arrayOf(253,219,199),
        arrayOf(255,255,255, 127),
        arrayOf(224,224,224),
        arrayOf(186,186,186),
        arrayOf(135,135,135),
        arrayOf(77,77,77),
        arrayOf(26,26,26)
    )

    val neg: Array<Array<Int>>
    val pos: Array<Array<Int>>
    if(clearSplitAtZero) {
        neg = RdYlBu.sliceArray(0..4).reversedArray()
        pos = RdYlBu.sliceArray(6..10)
    } else {
        neg = RdYlBu.sliceArray(0..5).reversedArray()
        pos = RdYlBu.sliceArray(5..10)
    }

    val negLayer = HeatmapLayer(unsafeJso<HeatmapLayerProps<Any?>> {
        id = "negLayer"
        this.data = data
        colorRange = neg
        if(normalized) {
            colorDomain = arrayOf(0, 1.0)
        }

        getPosition = { d : dynamic -> d.COORDINATES }
        if(normalized) {
            getWeight = { d: dynamic -> if(d != null) 1.0 - (2.0 * (d.WEIGHT as Double)) else null }
        } else {
            getWeight = { d: dynamic -> d.WEIGHT }
        }
        aggregation = "MEAN"
        threshold = 0
        this.intensity = intensity
        radiusPixels = 50 + (50 * screenScaleX)
    })

    val posLayer = HeatmapLayer(unsafeJso<HeatmapLayerProps<Any?>> {
        id = "posLayer"
        this.data = data
        colorRange = pos
        if(normalized) {
            colorDomain = arrayOf(0, 1.0)
        }

        getPosition = { d : dynamic -> d.COORDINATES }
        if(normalized) {
            getWeight = { d : dynamic -> if(d != null) -1.0 + (2.0 * (d.WEIGHT as Double)) else null }
        } else {
            getWeight = { d: dynamic -> d.WEIGHT }
        }
        aggregation = "MEAN"
        threshold = 0
        this.intensity = intensity
        radiusPixels = 50 + (50 * screenScaleX)
    })

    val colorRange = arrayOf(
        arrayOf(1, 152, 189),
        arrayOf(73, 227, 206),
        arrayOf(216, 254, 181),
        arrayOf(254, 237, 177),
        arrayOf(254, 173, 84),
        arrayOf(209, 55, 78)
    )

    val generatorsLayer = HeatmapLayer(unsafeJso<HeatmapLayerProps<Any?>> {
        id = "generatorsLayer"
        this.data = generatorsData

        getPosition = { d : dynamic -> d.COORDINATES }
        getWeight = { d: dynamic -> d.WEIGHT }
        this.colorRange = colorRange
        aggregation = "SUM"
        radiusPixels = 50 + (50 * screenScaleX)
    })

    val targetRef = useRef<HTMLDivElement>()

    ReactHTML.div {
        ref = targetRef
        css {
            position = Position.absolute
            this.width = 100.pct
            this.height = 100.pct
        }

        DeckGL {
            this.views = views
            initialViewState = INITIAL_VIEW_STATE
            controller = false
            layers = if(generators) arrayOf( generatorsLayer) else if(negative) arrayOf( negLayer, posLayer) else arrayOf( posLayer)
        }
    }

    useEffectOnce {
        val div = targetRef.current
        if (div != null) {
            val resizeObserver = ResizeObserver { entries, _ -> resize(entries, setScreenWidth, setScreenHeight) }
            println(div)
            println(div::class)
            resizeObserver.observe(div)

            awaitCancellation {
                resizeObserver.unobserve(div)
                resizeObserver.disconnect()
            }
        }
    }
}
